import { useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  PageHeader, KindChip, ReviewPill, SlaBadge, Avatar, Icon,
  ghostBtn, primaryBtn,
} from "@/components/admin-ui";
import { type Article } from "@/lib/admin-mock";
import { useAdminActions, useAdminArticles } from "@/lib/articles-hooks";
import { useAuthorMap } from "@/lib/authors-hooks";
import { useAuth } from "@/lib/auth";
import { ConfirmDialog, useToast } from "@/components/ui-prims";

export function ReviewQueuePage() {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const actions = useAdminActions();
  const toast = useToast();

  // The list hook handles mock-vs-live. Tab partitioning happens
  // client-side because we want the unclaimed / mine / queue counts
  // off the same fetched set.
  const { rows: all, refresh } = useAdminArticles({});
  const queue = all.filter(a => ["awaiting_review", "in_review", "changes_requested"].includes(a.state));
  const unclaimed = queue.filter(a => a.state === "awaiting_review");
  // "Assigned to you" matches articles whose reviewer_id is the
  // current admin. Falls back to the mock "You" string so the offline
  // seed in admin-mock still puts the right rows in this section.
  const mine = queue.filter(a =>
    (admin && a.reviewerId && a.reviewerId === admin.id) || a.reviewer === "You",
  );
  const overdue = queue.filter(a => a.sla === "overdue").length;

  // Bulk-approve selection — only "in_review" rows assigned to the
  // current admin can be bulk-approved. The set is keyed by article id.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const toggleSelected = (id: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const bulkApprove = async () => {
    if (selected.size === 0 || bulkBusy) return;
    setBulkBusy(true);
    setConfirmBulk(false);
    let ok = 0;
    let skipped = 0;
    try {
      // Sequential to keep ordering deterministic and surface the first
      // 409 (e.g. another reviewer claimed) without losing the rest.
      for (const id of selected) {
        try {
          await actions.approve(id, { publish: "now" });
          ok++;
        } catch (err) {
          console.warn("bulk approve: skip", id, err);
          skipped++;
        }
      }
      setSelected(new Set());
      refresh();
      if (skipped === 0) {
        toast(`Approved ${ok} article${ok === 1 ? "" : "s"}`, { tone: "success" });
      } else {
        toast(`Approved ${ok} · Skipped ${skipped}`, { tone: "info" });
      }
    } finally {
      setBulkBusy(false);
    }
  };

  // Real-data stat sub-lines. Each falls back to "—" when the queue is
  // empty so the strip never shows a stale or misleading number.
  const distinctAuthors = new Set(
    queue.map(a => a.authorDisplay?.id ?? a.author).filter(Boolean),
  ).size;
  const oldestUnclaimed = unclaimed.length > 0 ? oldestWaitLabel(unclaimed) : "—";
  const medianWait = queue.length > 0 ? medianWaitLabel(queue) : "—";

  // "Cleared (last 30d)" deep-links to All articles → Published tab.
  // The full date filter is a future iteration; the tab handoff already
  // gets the reviewer to the right view without a stale stub.
  const goCleared = () => navigate("/editorial/articles?tab=published");

  // Bulk-approve is only meaningful against rows you're holding. The
  // button is enabled iff at least one row is checked in "Assigned to
  // you" — checked rows are tracked in `selected`.
  const bulkEligible = selected.size > 0 && actions.enabled;

  return (
    <>
      <PageHeader
        kicker="Editorial"
        title="Review queue"
        sub="Articles submitted for editorial sign-off — from agents and the ProProp Data Lab desk. Claim a piece to take it out of the unclaimed pool."
        actions={
          <>
            <button style={ghostBtn} type="button" onClick={goCleared}>
              {Icon.history} Cleared (last 30d)
            </button>
            <button
              style={{ ...primaryBtn, opacity: bulkEligible ? 1 : 0.5, cursor: bulkEligible ? "pointer" : "not-allowed" }}
              type="button"
              disabled={!bulkEligible || bulkBusy}
              onClick={() => setConfirmBulk(true)}
              title={
                !actions.enabled
                  ? "Live API not enabled in this build"
                  : selected.size === 0
                  ? "Tick rows in “Assigned to you” to enable bulk approve"
                  : `Approve & publish ${selected.size} now`
              }
            >
              {Icon.check} {bulkBusy ? "Approving…" : selected.size > 0 ? `Bulk approve (${selected.size})` : "Bulk approve…"}
            </button>
          </>
        }
      />

      <div style={statsStrip}>
        <Stat label="In the queue"    value={queue.length}     sub={`${distinctAuthors} author${distinctAuthors === 1 ? "" : "s"}`} />
        <Stat label="Unclaimed"       value={unclaimed.length} sub={unclaimed.length > 0 ? `oldest ${oldestUnclaimed}` : "—"} accent="warn" />
        <Stat label="Assigned to you" value={mine.length}      sub={`${mine.filter(m => m.sla === "overdue").length} overdue`} accent="accent" />
        <Stat label="SLA breaches"    value={overdue}          sub="48-hour target" accent={overdue > 0 ? "neg" : "mute"} />
        <Stat label="Median wait"     value={medianWait}       sub="across queue" />
      </div>

      <Section title="Unclaimed" count={unclaimed.length} desc="First-come, first-claim. Older submissions float to the top.">
        <List articles={unclaimed} onChanged={refresh} />
      </Section>

      <Section title="Assigned to you" count={mine.length} desc="You're the named reviewer. Tick rows for bulk approve.">
        <List
          articles={mine}
          onChanged={refresh}
          selectable
          selected={selected}
          onToggleSelected={toggleSelected}
        />
      </Section>

      <div style={{ height: 22 }} />

      <ConfirmDialog
        open={confirmBulk}
        title={`Approve ${selected.size} article${selected.size === 1 ? "" : "s"}?`}
        body={
          <>
            All selected articles will be approved and published immediately.
            This cannot be undone — readers will see them within the next cron tick.
          </>
        }
        confirmLabel={`Approve & publish ${selected.size}`}
        confirmTone="primary"
        busy={bulkBusy}
        onConfirm={() => void bulkApprove()}
        onCancel={() => setConfirmBulk(false)}
      />
    </>
  );
}

// medianWaitLabel computes the median (now - submittedAt) across queue
// items that carry a real ISO timestamp. Falls back to "—" when fewer
// than 1 row has one (mock mode, or a race before the first response
// lands).
function medianWaitLabel(rows: Article[]): string {
  const now = Date.now();
  const secs = rows
    .map(a => a.submittedAt ? (now - new Date(a.submittedAt).getTime()) / 1000 : null)
    .filter((x): x is number => x != null && Number.isFinite(x) && x >= 0)
    .sort((a, b) => a - b);
  if (secs.length === 0) return "—";
  const mid = secs.length % 2 === 1
    ? secs[(secs.length - 1) >> 1]
    : (secs[secs.length / 2 - 1] + secs[secs.length / 2]) / 2;
  return formatDuration(mid);
}

function oldestWaitLabel(rows: Article[]): string {
  const now = Date.now();
  const oldest = rows
    .map(a => a.submittedAt ? (now - new Date(a.submittedAt).getTime()) / 1000 : null)
    .filter((x): x is number => x != null && Number.isFinite(x) && x >= 0)
    .reduce((max, x) => Math.max(max, x), 0);
  if (oldest === 0) return "—";
  return formatDuration(oldest);
}

function formatDuration(secs: number): string {
  if (secs < 60) return `${Math.round(secs)}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  if (secs < 86400) {
    const h = Math.floor(secs / 3600);
    const m = Math.round((secs % 3600) / 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(secs / 86400);
  const h = Math.round((secs % 86400) / 3600);
  return h > 0 ? `${d}d ${h}h` : `${d}d`;
}

function Stat({ label, value, sub, accent }: { label: string; value: ReactNode; sub: string; accent?: "warn" | "neg" | "accent" | "mute" }) {
  const fg =
    accent === "warn"   ? "#8a5a00" :
    accent === "neg"    ? "#7a2d1f" :
    accent === "accent" ? "var(--accent)" :
                          "var(--ink)";
  return (
    <div style={{ flex: 1, padding: "14px 18px", borderRight: "1px solid var(--rule)" }}>
      <div
        style={{
          fontFamily: "var(--font-ui)", fontSize: 10, color: "var(--ink-4)",
          letterSpacing: "0.13em", textTransform: "uppercase", fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "var(--font-serif)", fontSize: 28, fontWeight: 500,
          letterSpacing: "-0.025em", color: fg, lineHeight: 1, marginTop: 7,
        }}
      >
        {value}
      </div>
      <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)", marginTop: 5 }}>{sub}</div>
    </div>
  );
}

function Section({ title, count, desc, children }: { title: string; count: number; desc: string; children: ReactNode }) {
  return (
    <section style={{ padding: "4px 26px 22px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
        <h2
          style={{
            fontFamily: "var(--font-serif)", fontSize: 19, fontWeight: 500,
            letterSpacing: "-0.02em", margin: 0, color: "var(--ink)",
          }}
        >
          {title}
        </h2>
        <span
          style={{
            fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-4)",
            background: "var(--surface-2)", padding: "1px 7px", borderRadius: 999,
          }}
        >
          {count}
        </span>
        <span
          style={{
            fontFamily: "var(--font-serif)", fontSize: 12.5, fontStyle: "italic",
            color: "var(--ink-3)", marginLeft: 4,
          }}
        >
          — {desc}
        </span>
      </div>
      {children}
    </section>
  );
}

function List({
  articles,
  onChanged,
  selectable = false,
  selected,
  onToggleSelected,
}: {
  articles: Article[];
  onChanged: () => void;
  selectable?: boolean;
  selected?: Set<string>;
  onToggleSelected?: (id: string) => void;
}) {
  const navigate = useNavigate();
  const actions = useAdminActions();
  // Tracks which row is mid-claim so we can disable the button. Keyed
  // by article id; only one row is ever claimed at a time in practice.
  const [busyId, setBusyId] = useState<string | null>(null);
  // Live author lookup — articles carry real auth_users UUIDs, not
  // the hardcoded mock AuthorKey, so AUTHORS[a.author] would crash.
  const { resolve: resolveAuthor } = useAuthorMap();

  // Claim flow per user feedback: take ownership, refresh the queue,
  // and leave the reviewer on the queue page — the row moves from
  // Unclaimed to Assigned to you on its own. Open is the dedicated
  // path to the review screen.
  const claim = async (id: string) => {
    if (busyId) return;
    setBusyId(id);
    try {
      await actions.claim(id);
      onChanged();
    } finally {
      setBusyId(null);
    }
  };

  // Grid template adds a checkbox column when selectable. Padding-left
  // shifts in too so the checkbox doesn't collide with the SLA spine.
  const gridTemplate = selectable
    ? "24px 6px 80px 1fr 130px 150px 180px"
    : "6px 80px 1fr 130px 150px 180px";

  if (articles.length === 0) {
    return (
      <div
        style={{
          padding: "22px 18px", textAlign: "center", border: "1px dashed var(--rule)",
          borderRadius: 10, background: "var(--surface)",
          fontFamily: "var(--font-serif)", fontStyle: "italic",
          color: "var(--ink-3)", fontSize: 13,
        }}
      >
        Nothing here. ✓
      </div>
    );
  }
  return (
    <div style={{ borderRadius: 10, border: "1px solid var(--rule)", background: "var(--surface)", overflow: "hidden" }}>
      {articles.map((a, i) => {
        const age = a.submitted.replace(" ago", "");
        // Prefer the inline author snippet the backend joins on list
        // responses — falls back to /admin/authors lookup only when the
        // wire didn't carry it (mock mode).
        const author = a.authorDisplay ?? resolveAuthor(a.author).display;
        return (
          <div
            key={a.id}
            style={{
              display: "grid",
              gridTemplateColumns: gridTemplate,
              gap: 14, alignItems: "center",
              padding: "14px 16px 14px 12px",
              borderTop: i === 0 ? "none" : "1px solid var(--rule)",
              cursor: "pointer",
              transition: "background 120ms",
            }}
            onClick={() => navigate(`/editorial/articles/${a.id}`)}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-2)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            {selectable && (
              <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <input
                  type="checkbox"
                  style={{ accentColor: "var(--accent)" }}
                  checked={selected?.has(a.id) ?? false}
                  onChange={() => onToggleSelected?.(a.id)}
                  aria-label="Select for bulk action"
                />
              </div>
            )}
            <div
              style={{
                width: 4, height: 36, borderRadius: 2,
                background: a.sla === "overdue" ? "#b04a32" : "#1d4a3a",
                opacity: a.sla === "overdue" ? 1 : 0.55,
              }}
            />
            <div style={{ display: "flex", alignItems: "center" }}>
              <KindChip kind={a.kind} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontFamily: "var(--font-serif)", fontSize: 14.5, fontWeight: 500,
                  color: "var(--ink)", letterSpacing: "-0.012em",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  marginBottom: 4,
                }}
              >
                {a.title}
              </div>
              <div
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--ink-3)",
                }}
              >
                <Avatar initials={author.initials} tint={author.tint} size={18} title={author.name} />
                <span>{author.name}</span>
                <Dot />
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5 }}>v{a.version}</span>
                <Dot />
                <span>{a.words.toLocaleString()} words · {a.readTime}</span>
                {a.flags > 0 && (
                  <>
                    <Dot />
                    <span style={{ color: "#b04a32", display: "inline-flex", alignItems: "center", gap: 4 }}>
                      {Icon.alert} {a.flags} editorial flag{a.flags === 1 ? "" : "s"}
                    </span>
                  </>
                )}
              </div>
            </div>
            <div>
              <SlaBadge sla={a.sla} age={age} />
            </div>
            <div>
              <ReviewPill state={a.state} />
            </div>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
              <button
                style={secondaryBtn}
                type="button"
                onClick={() => navigate(`/editorial/articles/${a.id}`)}
              >
                {Icon.eye} Open
              </button>
              {a.state === "awaiting_review" && (
                <button
                  style={smallPrimary}
                  type="button"
                  disabled={busyId === a.id || !actions.enabled}
                  onClick={() => void claim(a.id)}
                >
                  {busyId === a.id ? "Claiming…" : "Claim"}
                </button>
              )}
              {a.state === "in_review" && (
                <button
                  style={smallPrimary}
                  type="button"
                  onClick={() => navigate(`/editorial/articles/${a.id}`)}
                >
                  Continue
                </button>
              )}
              {a.state === "changes_requested" && (
                <button
                  style={smallPrimary}
                  type="button"
                  onClick={() => navigate(`/editorial/articles/${a.id}`)}
                >
                  Re-check
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Dot() {
  return <span style={{ color: "var(--ink-4)" }}>·</span>;
}

const statsStrip: CSSProperties = {
  margin: "0 26px 22px", borderRadius: 12,
  border: "1px solid var(--rule)", background: "var(--surface)",
  display: "flex",
};
const secondaryBtn: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 5,
  padding: "5px 10px", borderRadius: 6,
  background: "var(--surface)", border: "1px solid var(--rule)",
  color: "var(--ink-2)", cursor: "pointer",
  fontFamily: "var(--font-ui)", fontSize: 11.5, fontWeight: 500,
};
const smallPrimary: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "5px 12px", borderRadius: 7,
  background: "var(--accent)", border: "none",
  color: "#f4f1ea", cursor: "pointer",
  fontFamily: "var(--font-ui)", fontSize: 11.5, fontWeight: 500,
};
